import { Stack } from '@mui/material'
import React from 'react'
import Button from '@mui/material/Button'
import SendIcon from '@mui/icons-material/Send'
import Dialog from '@mui/material/Dialog'
import DialogActions from '@mui/material/DialogActions'
import DialogContent from '@mui/material/DialogContent'
import DialogTitle from '@mui/material/DialogTitle'
import { useDispatch, useSelector } from 'react-redux'
import { ProfileAccountTypeRadios } from '../../Profile/Form/radios'
import { getIsAchDebit, getProfileForm } from '../../Profile/selectors'
import { getIsFormValid, getIsFormValidAchDebit } from '../../Profile/utils'
import { useCallbacks } from '../../Profile/hooks/useCallbacks'
import { PaperComponent } from '../../Profile/styled'
import { ProfileFormComponent } from '../../Profile/Form'
import { getApiConfig } from '../../../utils/getApiConfig'
import { Theme } from '@mui/material/styles'
import { getUserInfo } from '../../App/selectors'
import { updateProfile } from '../../Profile/commands/profile/update'
import { useFirebase } from 'react-redux-firebase'
import { actions as appActions } from '../../App/reducer'
import { getProfileData } from '../../Profile/selectors'
import {
  updatePaymnet,
  updatePaymnetError,
} from '../../Profile/commands/payment/update'
import { getPaymentBody } from './utils'
import { onError } from '../../../utils/onError'

interface Props {
  onClose: () => void
}

export const ProfileUpdateModalComponent: React.FC<Props> = ({ onClose }) => {
  const dispatch = useDispatch()
  const profileForm = useSelector(getProfileForm)
  const userInfo = useSelector(getUserInfo)
  const { profile } = useSelector(getProfileData)
  const isAchDebit = useSelector(getIsAchDebit)
  const _updatePaymnetError = useSelector(updatePaymnetError)
  const firebase = useFirebase()

  const isFormValid = isAchDebit
    ? getIsFormValidAchDebit(profileForm)
    : getIsFormValid(profileForm)

  const { onBlured, blured, onChange, onIsAchDebit } = useCallbacks()

  React.useEffect(() => {
    if (_updatePaymnetError) onError('Update Paymnet Error')
  }, [_updatePaymnetError, onError])

  const onSubmit = React.useCallback(async () => {
    const configProfile = await getApiConfig({
      body: {
        customerProfileId: profile.customerProfileId,
        email: profileForm.email,
      },
    })

    const configPaymnet = await getApiConfig({
      body: getPaymentBody({
        profileForm,
        profile,
      }),
    })

    const firebaseUser = firebase.auth().currentUser
    const hasEmailChanged = userInfo?.email !== profileForm.email

    if (hasEmailChanged && firebaseUser != null) {
      firebaseUser
        .updateEmail(profileForm.email)
        .then(async () => {
          if (configPaymnet != null) updatePaymnet(configPaymnet)
          if (configProfile != null) updateProfile(configProfile)
          onClose()
        })
        .catch((error) => {
          dispatch(
            appActions.onAlert({
              severity: 'error',
              message: error.message,
            })
          )
        })
    } else {
      if (configPaymnet != null) updatePaymnet(configPaymnet)
      onClose()
    }
  }, [
    updatePaymnet,
    updateProfile,
    profileForm,
    userInfo,
    firebase,
    dispatch,
    profile,
    onClose,
  ])

  return (
    <Dialog fullWidth maxWidth="lg" open onClose={onClose}>
      <DialogTitle>Edit Profile</DialogTitle>
      <DialogContent
        sx={{
          pb: 1,
        }}
      >
        <p style={{ fontSize: 'small', fontStyle: 'italic' }}>
          In order to keep your financial information as secure as possible we
          do not store any of your information in our database.
          <br />
          Any changes to your billing information requires your credit card /
          banking information to be re-entered so we can verify it.
        </p>
        <PaperComponent variant="outlined">
          <Stack display="flex" flexDirection="column" gap={1}>
            <ProfileAccountTypeRadios
              isAchDebit={isAchDebit}
              onIsAchDebit={onIsAchDebit}
            />
            <ProfileFormComponent
              profileForm={profileForm}
              blured={blured}
              onChange={onChange}
              onBlured={onBlured}
              isAchDebit={isAchDebit}
              profile={profile}
            />
          </Stack>
        </PaperComponent>
      </DialogContent>
      <DialogActions
        sx={{
          ml: 2,
          mr: 2,
          justifyContent: 'space-between',
        }}
      >
        <Button
          sx={{
            '&:hover': {
              backgroundColor: 'transparent',
              color: (theme: Theme) => theme.palette.primary.light,
            },
          }}
          onClick={onClose}
        >
          Cancel
        </Button>
        <Button
          disabled={!isFormValid}
          variant="contained"
          startIcon={<SendIcon />}
          onClick={onSubmit}
        >
          Submit
        </Button>
      </DialogActions>
    </Dialog>
  )
}
